import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useCreditTransactions } from '@/hooks/useCreditTransactions';
import { Card } from '@/components/ui/card';

import SearchHeader from '../creditTransactions/SearchHeader';
import TransactionsTable from '../creditTransactions/TransactionsTable';
import EmptyState from '../creditTransactions/EmptyState';
import Pagination from '../analysisHistory/Pagination';

const CreditTransactions = () => {
    const { user } = useAuth();
    const {
        transactions,
        loading,
        error,
        searchTerm,
        setSearchTerm,
        transactionType,
        setTransactionType,
        getTransactionTypeLabel,
        formatTransactionId,
        formatReferenceId,
        formatStripeId
    } = useCreditTransactions(user?.id);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 10;

    // Reset to first page when search term or transaction type changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, transactionType]);

    const hasTransactions = transactions.length > 0;
    const showEmptyState = !loading && !hasTransactions && !searchTerm && transactionType === 'all';
    const showNoResults = !loading && !hasTransactions && (searchTerm || transactionType !== 'all');

    // Pagination calculations
    const totalEntries = transactions.length;
    const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedTransactions = transactions.slice(startIndex, startIndex + entriesPerPage);

    return (
        <div className="container mx-auto py-8 px-4 space-y-8">
            <SearchHeader
                showSearch={!showEmptyState}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                transactionType={transactionType}
                setTransactionType={setTransactionType}
                isLoading={loading}
            />

            {error && (
                <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg mb-6" role="alert">
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            <Card>
                {showEmptyState ? (
                    <EmptyState />
                ) : showNoResults ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <p className="text-muted-foreground text-center">
                            No transactions found matching your criteria
                        </p>
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="overflow-x-auto">
                            <TransactionsTable
                                transactions={paginatedTransactions}
                                isLoading={loading}
                                getTransactionTypeLabel={getTransactionTypeLabel}
                                formatTransactionId={formatTransactionId}
                                formatReferenceId={formatReferenceId}
                                formatStripeId={formatStripeId}
                            />
                        </div>
                        {hasTransactions && (
                            <div className="pt-4">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    setCurrentPage={setCurrentPage}
                                    totalEntries={totalEntries}
                                    entriesPerPage={entriesPerPage}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default CreditTransactions;